import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { postAuthCodeSend, TwoFAEmailVerification } from '../../../api/auth';
import { secondsToWaitForSendingNewCode } from '../../../constants';
import AuthContext from '../../../context/AuthContext';
import { delay } from '../../../utils/miscellaneous';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { maskEmail } from '../../../utils/miscellaneous';

/**
 * Custom hook that fetches user data and sets email in the outlet context.
 *
 * @returns {Object} An object containing the user's email(the email in the outlet context extracted from the previous page assuming you are getting the email from the login page), router password, and masked email.
 *
 * @throws {Error} If no data is passed from the useLocation state
 */
export const useUserDataFetcher = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [ searchParams ] = useSearchParams();
  const { email, setEmail } = useOutletContext();

  // Extract data from URL params if coming from email link
  const emailFromUrl = searchParams.get( 'email' );
  const tokenFromUrl = searchParams.get( 'token' );

  const {
    email: routerEmail = emailFromUrl || undefined,
    password: routerPassword = tokenFromUrl || undefined,
    maskedEmail = emailFromUrl ? maskEmail( emailFromUrl ) : undefined,
    maskedPhone = undefined,
    params = undefined,
  } = state || {};

  useEffect( () => {
    // Only navigate away if both email and password/token are undefined
    if ( routerEmail === undefined && routerPassword === undefined ) {
      navigate( '/start' );
      return;
    }

    // If we have email from URL but not from router state, we're coming directly from email link
    if ( emailFromUrl && !state ) {
      // Need to fetch verification code again for this email
      // This will be handled in useAuthenticationLogic
    }

    setEmail( routerEmail );
  }, [ email, routerEmail, routerPassword ] );

  return {
    email: routerEmail,
    routerPassword,
    maskedEmail,
    maskedPhone,
    params,
    fromEmailLink: !!emailFromUrl && !state
  };
};
/**
 * Custom hook for authentication logic.
 *
 * @param {string} email - The user's email.
 * @param {string} routerPassword - The router password.
 * @param {string} factor - The factor for verification (e.g., "email" or "phone").
 * @returns {Object} - An object containing state variables and functions for authentication logic.
 */
export const useAuthenticationLogic = ( email, routerPassword, factor, params, fromEmailLink ) => {
  const [ code, setCode ] = useState( '' );
  const [ incorrect, setIncorrect ] = useState( false );
  const [ sendNewCodeActivated, setSendNewCodeActivated ] = useState( false );
  const [ isLoading, setIsLoading ] = useState( true );
  const [ responseReturned, setResponseReturned ] = useState( false );
  const [ errorMessage, setErrorMessage ] = useState();
  const { loginUser, authTokenSetter, clearAutharizationIfError } = useContext( AuthContext );

  useEffect( () => {
    if ( email === '' ) return;

    // If accessing directly from email link, DON'T request new verification code
    if ( fromEmailLink ) {
      // Don't call handleSendAuthCodeForDirectAccess()
      // Just set the states as if we've already sent the code
      setResponseReturned( true );
      setIsLoading( false );

      delay( secondsToWaitForSendingNewCode ).then( () => {
        setSendNewCodeActivated( true );
      } );
    } else {
      setResponseReturned( true );
      setIsLoading( false );

      delay( secondsToWaitForSendingNewCode ).then( () => {
        setSendNewCodeActivated( true );
      } );
    }
  }, [ email, fromEmailLink ] );


  const handleSendAuthCode = async () => {
    setIncorrect( false );
    setErrorMessage( '' );
    setCode( '' );
    setSendNewCodeActivated( false );
    delay( secondsToWaitForSendingNewCode ).then( () => {
      setSendNewCodeActivated( true );
    } );
    if ( params ) {
      await resendVerifyCodeForGoogleLogin()
    } else {
      loginUser( email, routerPassword, factor ).catch( () => {
        setIncorrect( true );
        setErrorMessage( 'Error sending the new code. Please try again' );
      } );
    };
  }
  //call to verify new2faEmailVerification
  const handle2faEmailVerification = async ( email, password, factor, code ) => {
    try {
      setIsLoading( true );
      const res = await TwoFAEmailVerification( email, password, factor, code );

      // Successful response, save the auth token and return the response
      authTokenSetter( res );
      setIncorrect( false )
      setIsLoading( false );
      return res;

    } catch ( error ) {
      console.log( error );
      setIsLoading( false );
      clearAutharizationIfError();
      setIncorrect( true );
      if ( !error.response ) {
        // If there's no response, it means the request failed at the network level
        setErrorMessage( 'An unexpected error occurred. Please try again.' );
      }
      if ( error.response.status === 401 ) {
        // Invalid credentials
        setErrorMessage( 'We couldn’t find an account with that email. Please check your credentials or sign up.' );
      } else if ( error.response.status === 403 ) {
        // Wrong verification code
        setErrorMessage( 'Please insert a valid code' );
      } else {
        // Handle any other errors
        setErrorMessage( 'An unexpected error occurred. Please try again.' );
      }
      return error.response;
    }
  };


  const resendVerifyCodeForGoogleLogin = async () => {
    try {
      const paramsFromObject = params && new URLSearchParams( params ); // Convert the object to URLSearchParams

      const gsiTokenResponseFromObject = await axios.post( process.env.REACT_APP_AIMD_API_URL + '/gsi-token', paramsFromObject, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      } );

    } catch ( error ) {
      setIncorrect( true );
      setErrorMessage( 'Error sending the new code. Please try again' );
    }
  };

  const handleAuthenticate = async () => {
    try {
      if ( code.length !== 6 ) {
        setIncorrect( true );
        setErrorMessage( 'Please insert a valid code' );
        return;
      }
      setIncorrect( false );
      const verifyingReq = await handle2faEmailVerification( email, routerPassword, factor, code ); //if the loginUser function is successful it will redirect to /demographics

    } catch ( error ) {
      setIncorrect( true );
      setErrorMessage( 'Please insert a valid code' );
    }
  };

  return {
    code,
    setCode,
    incorrect,
    errorMessage,
    sendNewCodeActivated,
    isLoading,
    responseReturned,
    handleSendAuthCode,
    handleAuthenticate,
  };
};